
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { plainToInstance } from "class-transformer";

import unit_routes from "@/api/routes/units";

import { phases } from "@/consts";
import { Debounce } from "@/helpers";
import CUpload from "@/components/Upload.vue";
import CImageView from "@/components/ImageView.vue";
import CInfiniteScroll from "@/components/InfiniteScroll.vue";

import RelationVersion from "@/models/relation/version";
import UnitVersion, { EUnitableType } from "@/models/unit/unit_version";
import TestVersion from "@/models/unit/unitable/test/version";
import { questionTypes } from "@/models/unit/unitable/test/question";
import TestQuestionVersion from "@/models/unit/unitable/test/question_version";
import TestAnswerVersion from "@/models/unit/unitable/test/answer_version";
import UnitUnit from "@/models/unit/unit";

interface IData {
  readonly current_page: number;
  readonly next_page: boolean;
}

type TTesting = Pick<UnitVersion, "id" | "title">;

interface ITestingsData extends IData {
  readonly unit_versions: TTesting[];
}

@Component({
  name: "CProgramMaterialTest",
  components: {
    CUpload,
    CImageView,
    CInfiniteScroll
  }
})
export default class CProgramMaterialTest extends Vue {
  @Prop({ required: true }) program_id!: string;
  @Prop({ required: true }) relation!: RelationVersion;
  @Prop({ required: false, default: false }) public disabled!: boolean;

  public phases = phases.slice(1, 5);
  public unit_version: UnitVersion | null = null;

  public question_types = questionTypes;
  public can_reanswer_options = [
    { label: "Разрешить повторное прохождение", value: true },
    { label: "Запретить повторное прохождение", value: false }
  ];
  public use_image_options = [
    { label: "Использовать", value: true },
    { label: "Не использовать", value: false }
  ];
  public accepted_image_types: string[] = ["image/png", "image/jpeg"];

  private preload_infinite: boolean = false;
  private per_page: number = 100;
  private current_page: number = 0;
  private next_page: boolean = false;
  public templates_search: string = "";
  public templates: TTesting[] = [];

  public show_select_templates_list: boolean = true;

  protected async created() {
    if (this.relation.id) {
      this.show_select_templates_list = false;

      await this.loadTest(this.relation.unit_version_id);
    } else {
      this.templates = [];

      await this.loadTestsList();
    }
  }

  public async createNewTest() {
    this.show_select_templates_list = false;

    this.unit_version = new UnitVersion({
      relation_version: this.relation,
      unit: new UnitUnit({ is_template: false }),
      unitable: new TestVersion(),
      unitable_type: EUnitableType["Test::Version"]
    });

    this.unit_version!.title = "Тест";
  }

  public async createNewTestFromTemplate(unit_version_id: string) {
    this.show_select_templates_list = false;

    await this.loadTest(unit_version_id);
  }

  public async loadTest(unit_version_id: string) {
    return this.$api
      .get(unit_routes.version(unit_version_id))
      .then(({ data: res }: { data: UnitVersion }) => {
        this.unit_version = plainToInstance(UnitVersion, res);
      });
  }

  @Watch("templates_search")
  @Debounce(500)
  protected async changeSearch() {
    this.templates = [];

    await this.loadTestsList();
  }

  public async infiniteHandler() {
    if (this.next_page && !this.preload_infinite) {
      await this.loadTestsList(this.current_page + 1);
    }
  }

  public disableEdit() {
    return this.disabled || !!this.unit_version?.unit?.is_template;
  }

  public unitable() {
    return this.unit_version?.unitable as TestVersion;
  }

  private async loadTestsList(page: number = 0) {
    this.preload_infinite = true;

    return this.$api
      .get(unit_routes.versions, {
        params: {
          page,
          per_page: this.per_page,
          paginate: true,
          search: this.templates_search,
          unitable_type: EUnitableType["Test::Version"],
          program_version_id: this.relation.program_version_id,
          relation_level: this.relation.level
        }
      })
      .then(({ data: res }: { data: ITestingsData }) => {
        this.current_page = res.current_page;
        this.next_page = res.next_page;

        this.templates.push(...res.unit_versions);
      })
      .finally(() => {
        this.preload_infinite = false;
      });
  }

  public addQuestion() {
    const questions_count = this.unitable().question_versions.length;

    this.unitable().question_versions.push(
      new TestQuestionVersion({
        position: questions_count + 1
      })
    );
  }

  public removeQuestion(qv_index: number) {
    this.unitable().question_versions.splice(qv_index, 1);

    this.unitable().question_versions.forEach(
      (qv, index) => (qv.position = index + 1)
    );
  }

  public changeQuestionType(question_version: TestQuestionVersion) {
    question_version.answer_versions.forEach(
      av => (av.answer.is_correct = false)
    );
  }

  public addAnswer(event: KeyboardEvent, qv_index: number) {
    const question_version = this.unitable().question_versions[qv_index];

    if (
      event.target &&
      event.target instanceof HTMLInputElement &&
      event.target.value.length
    ) {
      const answers_count = question_version.answer_versions.length;

      question_version.answer_versions.push(
        new TestAnswerVersion({
          value: event.target.value,
          position: answers_count + 1
        })
      );

      event.target.value = "";
    }
  }

  public removeAnswer(question_version: TestQuestionVersion, av_index: number) {
    question_version.answer_versions.splice(av_index, 1);

    question_version.answer_versions.forEach(
      (av, index) => (av.position = index + 1)
    );
  }

  public selectCorrectAnswer(
    question_version: TestQuestionVersion,
    av_index: number
  ) {
    if (question_version.isSingle) {
      question_version.answer_versions.forEach((answer_version, index) => {
        if (index !== av_index) {
          answer_version.answer.is_correct = false;
        }
      });
    }
  }

  public cancelAdd() {
    this.$emit("close", true);
  }

  public async saveTesting() {
    if (this.unit_version) {
      if (this.unit_version.unit!.is_template) {
        // ! Если материал из шаблона
        if (this.relation.id) {
          // ! И он уже добавлен, то просто выходим
          this.$emit("relationUpdated", this.relation);
        } else {
          // ! Иначе добавляем
          return this.$api
            .post(unit_routes.relations, {
              unit_version_id: this.unit_version.id,
              program_id: this.program_id,
              level: this.relation.level
            })
            .then(({ data: res }: { data: RelationVersion }) => {
              this.$emit("relationCreated", res);
            })
            .catch(({ response: res }) => {
              this.$notify({
                group: "notifications",
                type: "error",
                text: res.data.error,
                speed: 500
              });
            });
        }
      } else {
        // ! Если материал не из шаблона
        if (this.relation.id) {
          // ! И он уже добавлен, то обновляем
          return this.$api
            .put(unit_routes.version(this.unit_version!.id), {
              ...this.unit_version,
              ...{
                relation_version: {
                  id: this.relation.id,
                  level: this.relation.level
                }
              }
            })
            .then(({ data: res }: { data: RelationVersion }) => {
              this.$emit("relationUpdated", res);
            })
            .catch(({ response: res }) => {
              this.$notify({
                group: "notifications",
                type: "error",
                text: res.data.error,
                speed: 500
              });
            });
        } else {
          // ! Иначе создаем
          return this.$api
            .post(unit_routes.versions, {
              ...this.unit_version,
              ...{ relation: { program_id: this.program_id } }
            })
            .then(({ data: res }: { data: RelationVersion }) => {
              this.$emit("relationCreated", res);
            })
            .catch(({ response: res }) => {
              this.$notify({
                group: "notifications",
                type: "error",
                text: res.data.error,
                speed: 500
              });
            });
        }
      }
    }
  }
}
